/**
 * SEO component that queries for data with
 * Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.com/docs/how-to/querying-data/use-static-query/
 */

import * as React from "react"
import { Helmet } from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"

function Seo({ 
  description, 
  title, 
  children, 
  path = "/", 
  image = "", 
  articleData = null,
  publishDate,
  lastModifiedDate,
  author,
  keywords,
  canonicalUrl,
  breadcrumbs = null
}) {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            author
            siteUrl
          }
        }
      }
    `
  )

  const metaDescription = description || site.siteMetadata?.description
  const defaultTitle = site.siteMetadata?.title
  const siteUrl = site.siteMetadata?.siteUrl || ''
  const ogImage = image || `${siteUrl}/og-image.jpg` // Default OG image
  const pageTitle = title ? `${title} | ${defaultTitle}` : defaultTitle
  const pageAuthor = author || site.siteMetadata?.author || ''
  const pageKeywords = keywords || `california, sierra nevada, camping, campgrounds, hiking, trails, maps, guides, backpacking, overlanding, outdoor recreation, wilderness, reservations`
  const pageCanonicalUrl = canonicalUrl || `${siteUrl}${path}`

  // Create or update articleData with the new fields if they exist
  const updatedArticleData = publishDate ? {
    ...articleData || {},
    title: title || (articleData?.title || pageTitle),
    publishDate: publishDate,
    modifiedDate: lastModifiedDate || publishDate,
    author: author || (articleData?.author || site.siteMetadata?.author || '')
  } : articleData;

  // Schema.org structured data
  const schemaOrgWebPage = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    url: pageCanonicalUrl,
    headline: pageTitle,
    description: metaDescription,
    inLanguage: 'en-US',
    mainEntityOfPage: pageCanonicalUrl,
    name: pageTitle,
    author: {
      '@type': 'Organization',
      name: pageAuthor,
    },
    publisher: {
      '@type': 'Organization',
      name: defaultTitle,
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/logo.png`,
      },
    },
  }

  // Add article schema if article data is provided
  const schemaArticle = updatedArticleData ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: updatedArticleData.title || pageTitle,
    image: ogImage,
    datePublished: updatedArticleData.publishDate,
    dateModified: updatedArticleData.modifiedDate || updatedArticleData.publishDate,
    author: {
      '@type': 'Person',
      name: updatedArticleData.author || pageAuthor,
    },
    publisher: {
      '@type': 'Organization',
      name: defaultTitle,
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/logo.png`,
      },
    },
    description: metaDescription,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': pageCanonicalUrl,
    },
  } : null;

  // Breadcrumb schema
  const breadcrumbSchema = breadcrumbs ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: `${siteUrl}${crumb.path}`
    }))
  } : null;

  // Site Navigation Schema - only for homepage
  const siteNavigationSchema = path === '/' ? {
    '@context': 'https://schema.org',
    '@type': 'SiteNavigationElement',
    name: 'Main Navigation',
    url: siteUrl,
    hasPart: [
      {
        '@type': 'SiteNavigationElement',
        name: 'Best Campgrounds',
        description: 'Interactive map showing the best campgrounds across California',
        url: `${siteUrl}/campground-viewer/`
      },
      {
        '@type': 'SiteNavigationElement', 
        name: 'Explore Guides',
        description: 'Comprehensive outdoor guides for beginners and pros',
        url: `${siteUrl}/guides/`
      },
      {
        '@type': 'SiteNavigationElement',
        name: 'Contact Us',
        description: 'Get in touch with Sierra Outdoors',
        url: `${siteUrl}/contact-us/`
      }
    ]
  } : null;

  // Organization schema - only for homepage
  const organizationSchema = path === '/' ? {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: defaultTitle,
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    description: metaDescription,
    sameAs: [
      'https://www.instagram.com/sierraoutdoorsorg',
      'https://www.youtube.com/@sierraoutdoors'
    ],
    potentialAction: {
      '@type': 'SearchAction',
      target: `${siteUrl}/guides?search={search_term_string}`,
      'query-input': 'required name=search_term_string'
    }
  } : null;

  return (
    <Helmet
      htmlAttributes={{ lang: 'en' }}
      title={pageTitle}
      meta={[
        {
          name: `description`,
          content: metaDescription,
        },
        {
          name: `keywords`,
          content: pageKeywords,
        },
        {
          name: `robots`,
          content: `index, follow`,
        },
        {
          property: `og:title`,
          content: pageTitle,
        },
        {
          property: `og:description`,
          content: metaDescription,
        },
        {
          property: `og:type`,
          content: updatedArticleData ? 'article' : 'website',
        },
        {
          property: `og:url`,
          content: pageCanonicalUrl,
        },
        {
          property: `og:image`,
          content: ogImage,
        },
        {
          property: `og:image:width`,
          content: `1200`,
        },
        {
          property: `og:image:height`,
          content: `630`,
        },
        {
          property: `og:site_name`,
          content: defaultTitle,
        },
        {
          property: `og:locale`,
          content: `en_US`,
        },
        // Add publish date and modified date meta tags for articles
        ...(publishDate ? [{
          property: `article:published_time`,
          content: publishDate,
        }] : []),
        ...(lastModifiedDate ? [{
          property: `article:modified_time`,
          content: lastModifiedDate,
        }] : []),
        {
          name: `twitter:card`,
          content: `summary_large_image`,
        },
        {
          name: `twitter:creator`,
          content: pageAuthor,
        },
        {
          name: `twitter:title`,
          content: pageTitle,
        },
        {
          name: `twitter:description`,
          content: metaDescription,
        },
        {
          name: `twitter:image`,
          content: ogImage,
        },
      ]}
      link={[
        {
          rel: "canonical",
          href: pageCanonicalUrl,
        },
      ]}
    >
      {/* Schema.org structured data */}
      <script type="application/ld+json">
        {JSON.stringify(schemaOrgWebPage)}
      </script>
      
      {updatedArticleData && (
        <script type="application/ld+json">
          {JSON.stringify(schemaArticle)}
        </script>
      )}

      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}

      {siteNavigationSchema && (
        <script type="application/ld+json">
          {JSON.stringify(siteNavigationSchema)}
        </script>
      )}

      {organizationSchema && (
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      )}
      
      {children}
    </Helmet>
  )
}

export default Seo
